import { useMemo } from 'react'
import { t } from 'ttag'
import { CustomTypeCategory, customUnits } from '../../../../services/CustomTypes'
import { unitsCountDurationMinuteLong, unitsCountEnergyKilocalorieShort } from '../../../../common/helpers/units'
import { consistencyUnit } from '../../ChallengeAdmin'

/**
 * Get the unit text for daily goal based on activity type.
 * Memoized to prevent input from losing focus on re-render.
 */
export const useDailyGoalUnit = (
  activityType: CustomTypeCategory,
  customType: string | null | undefined,
  customUnit: string,
): string => {
  return useMemo(() => {
    if (activityType === CustomTypeCategory.STEPS) {
      return t`steps`
    }
    if (activityType === CustomTypeCategory.INTENSIVE_MINUTES) {
      return unitsCountDurationMinuteLong
    }
    if (activityType === CustomTypeCategory.CALORIES) {
      return unitsCountEnergyKilocalorieShort
    }
    if (activityType === CustomTypeCategory.ALL) {
      return '%'
    }
    // For custom types, use the custom unit text if available
    const customUnitText = customUnits.find((cu) => cu.id === customType)?.text
    return customUnitText || customUnit
  }, [activityType, customType, customUnit])
}

/**
 * Get the consistency unit text based on number of days.
 * Memoized to prevent input from losing focus on re-render.
 */
export const useConsistencyUnit = (days: number): string => {
  return useMemo(() => consistencyUnit(days), [days])
}

/**
 * Get the end adornment text for daily goal field.
 * For custom types, uses the custom unit value; otherwise uses the calculated unit text.
 */
export const useDailyGoalEndAdornment = (
  activityType: CustomTypeCategory,
  customUnit: string,
  dailyGoalUnit: string,
): string => {
  return useMemo(
    () => (activityType === CustomTypeCategory.CUSTOM ? customUnit : dailyGoalUnit),
    [activityType, customUnit, dailyGoalUnit],
  )
}

/**
 * Get the end adornment text for total goal field.
 * For custom types, uses the custom unit value; otherwise uses the custom unit field text.
 */
export const useTotalGoalEndAdornment = (
  activityType: CustomTypeCategory,
  customUnit: string,
  customUnitFieldWithText: string | undefined,
): string => {
  return useMemo(
    () => (activityType === CustomTypeCategory.CUSTOM ? customUnit : customUnitFieldWithText || ''),
    [activityType, customUnit, customUnitFieldWithText],
  )
}
